import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Trash2, Car, Calendar, Gauge, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';
import { useTheme } from '@/contexts/ThemeContext';
import { secureGetItem, secureRemoveItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';

interface RecentlyViewedVehiclesProps {
  config: any;
}

interface ViewedVehicle {
  id: number;
  timestamp: number;
}

export const RecentlyViewedVehicles = ({ config }: RecentlyViewedVehiclesProps) => {
  const { vehicles } = useAppData();
  const { selectedStore } = useStore();
  const { colors } = useTheme();
  const [recentlyViewed, setRecentlyViewed] = useState<any[]>([]);
  const [slugs, setSlugs] = useState<Record<number, string>>({});
  const [showArrows, setShowArrows] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const primaryColor = colors.primary;
  const buttonColor = colors.button;
  const titleColor = colors.title;

  // Determine filtering logic based on unifiedAds and unifiedAdsStore
  const shouldFilterByStore = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;

    // Don't filter if unifiedAds is false (already handled by useFilteredVehicles)
    if (!unifiedAds) return true;

    // Don't filter if unifiedAdsStore is null
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) return false;

    // Filter if selected store is different from unifiedAdsStore
    return selectedStoreId !== unifiedAdsStore;
  }, [config?.unifiedAds, config?.unifiedAdsStore, selectedStore?._id]);

  // Load recently viewed vehicles from localStorage
  useEffect(() => {
    const loadRecentlyViewed = async () => {
      try {
        const viewedStr = await secureGetItem('recentlyViewedVehicles');
        if (viewedStr) {
          const viewedIds: ViewedVehicle[] = JSON.parse(viewedStr);

          // Sort by timestamp (most recent first) and get last 10
          const sortedIds = viewedIds
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, 10);

          // Get vehicle data for each ID and preserve timestamp
          let viewedVehicles = sortedIds
            .map(viewed => {
              const vehicle = vehicles.find(v => v.id === viewed.id);
              return vehicle ? { ...vehicle, timestamp: viewed.timestamp } : null;
            })
            .filter(vehicle => vehicle !== null);

          // Filter by selected store if needed
          if (shouldFilterByStore && selectedStore?._id) {
            viewedVehicles = viewedVehicles.filter(vehicle => vehicle.store === selectedStore._id);
          }

          setRecentlyViewed(viewedVehicles);
        }
      } catch (error) {
        console.error('Erro ao carregar veículos vistos recentemente:', error);
      }
    };

    loadRecentlyViewed();

    // Listen for updates when vehicles are viewed
    const handleStorageChange = () => {
      loadRecentlyViewed();
    };

    window.addEventListener('vehicleViewed', handleStorageChange);
    return () => window.removeEventListener('vehicleViewed', handleStorageChange);
  }, [vehicles, shouldFilterByStore, selectedStore?._id]);

  // Load slugs for recently viewed vehicles
  useEffect(() => {
    const loadSlugs = async () => {
      const newSlugs: Record<number, string> = {};
      for (const vehicle of recentlyViewed) {
        newSlugs[vehicle.id] = await generateVehicleSlug(vehicle);
      }
      setSlugs(newSlugs);
    };
    if (recentlyViewed.length > 0) {
      loadSlugs();
    }
  }, [recentlyViewed]);

  const clearRecentlyViewed = async () => {
    await secureRemoveItem('recentlyViewedVehicles');
    setRecentlyViewed([]);
    window.dispatchEvent(new Event('vehicleViewed'));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));

    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} horas atrás`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} dias atrás`;
  };

  // Check if arrows should be shown based on scroll width vs client width
  useEffect(() => {
    const checkScrollNeeded = () => {
      // Use requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const needsScroll = container.scrollWidth > container.clientWidth;
          setShowArrows(needsScroll);
        }
      });
    };

    // Delay initial check to ensure DOM is ready
    const timeoutId = setTimeout(checkScrollNeeded, 0);

    // Throttle resize events to avoid excessive checks
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkScrollNeeded, 100);
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [recentlyViewed]);

  if (recentlyViewed.length === 0) {
    return null; // Don't render if no recently viewed vehicles
  }

  return (
    <section className="py-12 bg-muted/30 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Vehicles Carousel with Title Card */}
        <div className="grid grid-cols-1 md:grid-cols-[113px_1fr] gap-4">
          {/* Title Card - Fixed (não rola) */}
          <div className="hidden md:block">
            <Card className="border border-border/50 flex flex-col justify-between p-6 relative overflow-hidden h-full" style={{ backgroundColor: `${buttonColor}08`}}>
              {/* Vertical Text - Centered */}
              <div className="flex-1 flex items-center justify-center">
                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                  <h2 className="text-2xl font-bold whitespace-nowrap">
                    Últimos Vistos
                  </h2>
                </div>
              </div>

              <div className="flex justify-center">
                {/* Clear Button */}
                {recentlyViewed.length > 0 && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={clearRecentlyViewed}
                    className="rounded-full"
                    title="Limpar histórico"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          </div>

          {/* Vehicles Scroll Container */}
          <div className="relative group/carousel min-w-0 md:col-start-2">
            {/* Mobile Header - Only on mobile */}
            <div className="md:hidden mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: titleColor }}>
                      Últimos Vistos
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {Math.min(recentlyViewed.length, 5)} {Math.min(recentlyViewed.length, 5) === 1 ? 'veículo' : 'veículos'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearRecentlyViewed}
                  className="text-muted-foreground"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div
              ref={scrollContainerRef}
              id="recently-viewed-scroll"
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth h-[310px]"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >

              {/* Real Vehicles - Max 5 */}
              {recentlyViewed.slice(0, 5).map((vehicle, index) => {
                const vehicleSlug = slugs[vehicle.id] || '';
                return (
                  <Link
                    key={`${vehicle.id}-${index}`}
                    to={`/estoque/${vehicleSlug}`}
                    className="group flex-shrink-0 w-[240px] md:w-[235px] snap-start"
                  >
                    <Card
                      className="overflow-hidden transition-all duration-500 border border-transparent h-full"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = buttonColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      <CardContent className="p-0">
                        {/* Vehicle Image */}
                        <div className="relative h-[140px] overflow-hidden">
                          <img
                            src={vehicle.photos[0]}
                            alt={`${vehicle.brand} ${vehicle.model}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />

                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          {/* Time Badge */}
                          <div className="absolute top-3 left-3">
                            <Badge
                              className="backdrop-blur-md text-white text-xs font-medium border-0 px-2.5 py-1"
                              style={{ backgroundColor: primaryColor }}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              {formatTimeAgo(vehicle.timestamp)}
                            </Badge>
                          </div>

                          {/* Zero KM Badge */}
                          {vehicle.isZeroKm && (
                            <div className="absolute top-3 right-3">
                              <Badge className="bg-green-600/95 backdrop-blur-md text-white text-xs font-medium border-0 px-2.5 py-1">
                                Zero KM
                              </Badge>
                            </div>
                          )}
                        </div>

                        {/* Vehicle Info */}
                        <div className="p-4 space-y-3">
                          <div>
                            <h3 className="font-bold text-base text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                              {vehicle.brand} {vehicle.model}
                            </h3>
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                              {vehicle.version}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {vehicle.modelYear}
                            </span>
                            {vehicle.mileage >= 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Gauge className="h-3 w-3" />
                                  {vehicle.mileage === 0 ? 'Zero KM' : `${vehicle.mileage.toLocaleString('pt-BR')} km`}
                                </span>
                              </>
                            )}
                          </div>

                          <div className="pt-2 border-t border-border/50">
                            <div className="text-lg font-bold" style={{ color: titleColor }}>
                              {formatPrice(vehicle.price)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              Clique para ver detalhes →
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}

              {/* Placeholder Cards - Complement to 5 */}
              {recentlyViewed.length < 5 && Array.from({ length: 5 - recentlyViewed.length }).map((_, index) => (
                <div key={`placeholder-${index}`} className="flex-shrink-0 w-[240px] md:w-[235px] snap-start">
                  <Card className="overflow-hidden border-dashed border-border/50 bg-muted/20 flex flex-col h-full">
                    {/* Placeholder Image - Same aspect ratio as VehicleCard */}
                    <div className="relative aspect-[4/2.5] overflow-hidden flex-shrink-0 bg-muted/30 flex items-center justify-center">
                      <Eye className="h-8 w-8 text-muted-foreground/30" />
                    </div>

                    {/* Compact Info Section - Match VehicleCard structure */}
                    <CardContent className="p-4 flex flex-col">
                      {/* Title and Version - Fixed heights */}
                      <div className="mb-3 flex-shrink-0">
                        <div className="h-6 bg-muted/50 rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-muted/30 rounded w-1/2 mb-2"></div>
                        {/* Price Skeleton */}
                        <div className="h-7 bg-muted/50 rounded w-2/3"></div>
                      </div>

                      {/* Details - Single Row with fixed height */}
                      <div className="flex items-center gap-2 border-t border-border/50 pt-3 flex-shrink-0 h-8">
                        <div className="h-3 bg-muted/30 rounded w-16"></div>
                        <div className="w-1 h-1 bg-muted/30 rounded-full"></div>
                        <div className="h-3 bg-muted/30 rounded w-12"></div>
                      </div>

                      {/* CTA Space */}
                      <div className="h-4 mt-3 flex-shrink-0"></div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>

            {/* Navigation Arrows - Show only when content overflows */}
            {showArrows && (
              <>
                <button
                  onClick={() => {
                    const container = document.getElementById('recently-viewed-scroll');
                    if (container) {
                      const scrollAmount = 252; // 240px card + 12px gap
                      container.scrollBy({
                        left: -scrollAmount,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200 shadow-lg"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-800" />
                </button>
                <button
                  onClick={() => {
                    const container = document.getElementById('recently-viewed-scroll');
                    if (container) {
                      const scrollAmount = 252; // 240px card + 12px gap
                      container.scrollBy({
                        left: scrollAmount,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200 shadow-lg"
                  aria-label="Próximo"
                >
                  <ChevronRight className="h-5 w-5 text-gray-800" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
